// Inventory On Hand. Table with inline +/- adjustment + reorder-level edit.
// New rows via a modal at the top.
import { useEffect, useState } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch } from '../../lib/api'

interface Row {
  id: string; sku: string; name: string; stockOnHand: number; reorderLevel: number
  notes: string | null; lastAdjustedAt: string
  productId: string | null; variantId: string | null
}

export default function Inventory() {
  const { getAccessToken } = useAuth()
  const [rows, setRows] = useState<Row[]>([])
  const [search, setSearch] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [newOpen, setNewOpen] = useState(false)
  const [newRow, setNewRow] = useState({ sku: '', name: '', stockOnHand: 0, reorderLevel: 0, notes: '' })

  async function load() {
    try {
      const d = await apiFetch<{ items: Row[] }>(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/inventory${search ? `?search=${encodeURIComponent(search)}` : ''}`)
      setRows(d.items); setErr(null)
    } catch (e) { setErr((e as Error).message) }
  }
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  async function adjust(id: string, delta: number) {
    setBusy(id)
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/inventory/${id}`, { method: 'PATCH', body: JSON.stringify({ delta }) })
      await load()
    } catch (e) { alert((e as Error).message) } finally { setBusy(null) }
  }
  async function setReorder(id: string, n: number) {
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/inventory/${id}`, { method: 'PATCH', body: JSON.stringify({ reorderLevel: n }) })
      await load()
    } catch (e) { alert((e as Error).message) }
  }
  // Inline validation for the New SKU modal — replaces the previous
  // `alert()` so the user sees the problem next to the offending field.
  const [newErr, setNewErr] = useState<{ sku?: string; name?: string }>({})
  async function create() {
    const next: { sku?: string; name?: string } = {}
    if (!newRow.sku.trim())  next.sku  = 'SKU is required'
    if (!newRow.name.trim()) next.name = 'Name is required'
    setNewErr(next)
    if (Object.keys(next).length) return
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/inventory', { method: 'POST', body: JSON.stringify(newRow) })
      setNewRow({ sku: '', name: '', stockOnHand: 0, reorderLevel: 0, notes: '' })
      setNewErr({})
      setNewOpen(false)
      await load()
    } catch (e) { alert((e as Error).message) }
  }

  async function remove(id: string, sku: string) {
    if (!confirm(`Delete SKU ${sku}? Past order history is unaffected — this only removes the inventory row.`)) return
    setBusy(id)
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/inventory/${id}`, { method: 'DELETE' })
      await load()
    } catch (e) { alert((e as Error).message) } finally { setBusy(null) }
  }

  return (
    <div className="p-8">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="eyebrow">Production</div>
          <h1 className="font-heading uppercase font-bold text-3xl text-ink">Inventory On Hand</h1>
        </div>
        <div className="flex items-center gap-2">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search SKU or name…" className="input max-w-xs" />
          <button onClick={() => setNewOpen(true)} className="btn-gold">+ New SKU</button>
        </div>
      </div>

      {err && <div className="card text-red-700 mb-4">{err}</div>}

      <div className="card !p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface text-[10px] uppercase tracking-wider text-ink-3">
            <tr>
              <th className="text-left px-4 py-2 font-bold">SKU</th>
              <th className="text-left px-4 py-2 font-bold">Name</th>
              <th className="text-right px-4 py-2 font-bold">On hand</th>
              <th className="text-right px-4 py-2 font-bold">Reorder at</th>
              <th className="text-right px-4 py-2 font-bold">Last adjusted</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-ink-3 text-sm">No SKUs.</td></tr>}
            {rows.map((r) => {
              const low = r.stockOnHand <= r.reorderLevel
              return (
                <tr key={r.id} className="hover:bg-surface transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-ink-2">{r.sku}</td>
                  <td className="px-4 py-3 text-ink">{r.name}{r.notes && <div className="text-[11px] text-ink-3 truncate max-w-[280px]">{r.notes}</div>}</td>
                  <td className={'px-4 py-3 text-right font-semibold ' + (low ? 'text-red-700' : 'text-ink')}>
                    <div className="inline-flex items-center gap-1">
                      <button disabled={busy === r.id} onClick={() => adjust(r.id, -1)} className="w-6 h-6 rounded border border-border hover:border-gold text-ink-2 hover:text-ink disabled:opacity-50">−</button>
                      <span className="w-12 inline-block">{r.stockOnHand}</span>
                      <button disabled={busy === r.id} onClick={() => adjust(r.id, +1)} className="w-6 h-6 rounded border border-border hover:border-gold text-ink-2 hover:text-ink disabled:opacity-50">+</button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <input type="number" defaultValue={r.reorderLevel} min={0}
                      onBlur={(e) => { const n = Math.max(0, parseInt(e.target.value) || 0); if (n !== r.reorderLevel) setReorder(r.id, n) }}
                      className="w-16 px-2 py-1 bg-white border border-border rounded text-ink text-xs focus:outline-none focus:border-gold text-right" />
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-ink-3">{new Date(r.lastAdjustedAt).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">
                    <button disabled={busy === r.id} onClick={() => remove(r.id, r.sku)} className="text-xs text-red-600 hover:text-red-700 disabled:opacity-50">Delete</button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {newOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="font-heading uppercase font-bold text-lg text-ink mb-4">New SKU</h3>
            <div className="space-y-3">
              <div>
                <div className="eyebrow">SKU</div>
                <input value={newRow.sku} onChange={(e) => { setNewRow({ ...newRow, sku: e.target.value.toUpperCase() }); if (newErr.sku) setNewErr({ ...newErr, sku: undefined }) }}
                  className={'input ' + (newErr.sku ? '!border-red-400' : '')} />
                {newErr.sku && <div className="text-[11px] text-red-600 mt-1">{newErr.sku}</div>}
              </div>
              <div>
                <div className="eyebrow">Name</div>
                <input value={newRow.name} onChange={(e) => { setNewRow({ ...newRow, name: e.target.value }); if (newErr.name) setNewErr({ ...newErr, name: undefined }) }}
                  className={'input ' + (newErr.name ? '!border-red-400' : '')} />
                {newErr.name && <div className="text-[11px] text-red-600 mt-1">{newErr.name}</div>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><div className="eyebrow">Stock on hand</div><input type="number" min={0} value={newRow.stockOnHand} onChange={(e) => setNewRow({ ...newRow, stockOnHand: parseInt(e.target.value) || 0 })} className="input" /></div>
                <div><div className="eyebrow">Reorder level</div><input type="number" min={0} value={newRow.reorderLevel} onChange={(e) => setNewRow({ ...newRow, reorderLevel: parseInt(e.target.value) || 0 })} className="input" /></div>
              </div>
              <div><div className="eyebrow">Notes (optional)</div><input value={newRow.notes} onChange={(e) => setNewRow({ ...newRow, notes: e.target.value })} className="input" /></div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setNewOpen(false)} className="btn-outline flex-1 justify-center">Cancel</button>
              <button onClick={create} className="btn-gold flex-1 justify-center">Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
